import type {
  TenantContent,
  TenantEdition,
  TenantExam,
} from '../domain/entities/tenant.entity';

/** As seções que mudam conforme o funil da home: ensaio ou gabarito. */
type FunnelContent = Omit<TenantContent, 'previsoes' | 'youtubeVideoId'>;

/**
 * O texto padrão da LP, montado a partir da prova e da edição.
 *
 * Mesma estrutura da home do `enamed-feedback-lp` (hero, três passos, três
 * argumentos, fechamento e FAQ). **O funil segue o `gabaritoSubmission`**:
 * com o envio aberto, a home fala do gabarito (a prova já passou); antes
 * (`soon`) e depois (`closed`), do ensaio. Prova que tiver texto próprio
 * sobrescreve em `tenants.ts`.
 */
export function buildDefaultContent(
  exam: TenantExam,
  edition: Pick<
    TenantEdition,
    'year' | 'totalQuestions' | 'ensaioQuestionIds' | 'gabaritoSubmission'
  >,
): TenantContent {
  const { shortName } = exam;

  return {
    ...(edition.gabaritoSubmission === 'open'
      ? gabaritoContent(shortName, edition.year, edition.totalQuestions)
      : ensaioContent(shortName, edition.ensaioQuestionIds.length)),
    previsoes: {
      title: `Previsões Aristo para a ${shortName}`,
      description: `O material da Aristo sobre o que esperar da prova da ${shortName}, para você chegar no dia sabendo onde concentrar o estudo.`,
      ctaLabel: 'Ver as Previsões Aristo',
    },
  };
}

/**
 * Antes da prova: o produto é um **simulado** — a pessoa responde questões da
 * prova, cria a conta e recebe o diagnóstico.
 */
function ensaioContent(
  shortName: string,
  questionCount: number,
): FunnelContent {
  return {
    seo: {
      title: `Ensaio ${shortName}: responda e receba seu diagnóstico`,
      description: `Responda ${questionCount} questões da prova da ${shortName} e receba um diagnóstico de onde você está perdendo ponto.`,
    },
    hero: {
      title: `Faça o ensaio da ${shortName} e descubra onde você perde ponto`,
      lead: `Responda ${questionCount} questões da prova da ${shortName} e receba um diagnóstico e plano para aumentar seu desempenho até a prova.`,
      ctaLabel: 'Começar o ensaio',
      secondaryLabel: 'Enviar meu gabarito',
    },
    stepsSubtitle: 'Três passos entre o ensaio e o seu diagnóstico.',
    steps: [
      {
        title: 'Responda o ensaio',
        description: `São ${questionCount} questões da ${shortName}. Suas respostas ficam salvas no navegador enquanto você responde.`,
      },
      {
        title: 'Crie sua conta',
        description:
          'Ao terminar, faça um cadastro rápido para receber o seu resultado.',
      },
      {
        title: 'Receba seu diagnóstico',
        description:
          'Veja quantas questões você acertou e onde estão os seus erros: nas difíceis ou nas que estavam ao seu alcance.',
      },
    ],
    featuresTitle: 'O que o diagnóstico mostra',
    features: [
      {
        icon: 'chart',
        title: 'Desempenho por dificuldade',
        description:
          'Seus acertos e erros separados em questões fáceis, médias e difíceis.',
      },
      {
        icon: 'target',
        title: 'Onde o ponto escapou',
        description:
          'O diagnóstico aponta se você perdeu ponto em conteúdo ou em questões que estavam ao seu alcance.',
      },
      {
        icon: 'clock',
        title: 'No seu ritmo',
        description:
          'Pode parar e voltar depois: o que você marcou fica salvo neste navegador.',
      },
    ],
    closer: {
      title: 'Pronto para começar?',
      subtitle: `Responda o ensaio da ${shortName} e receba seu diagnóstico ao final.`,
    },
    faq: [
      {
        group: 'Sobre o ensaio',
        items: [
          {
            q: 'Preciso responder todas as questões?',
            a: 'Não, mas questões em branco contam como erro na prova real, e muitas em branco impedem a leitura do seu padrão de respostas.',
          },
          {
            q: 'Perco minhas respostas se fechar a página?',
            a: 'Não. Elas ficam salvas neste navegador até você enviar o ensaio.',
          },
          {
            q: `O diagnóstico prevê minha nota na ${shortName}?`,
            a: 'Não. Ele é uma leitura do seu desempenho neste ensaio, para orientar o estudo, e não uma previsão de nota.',
          },
        ],
      },
    ],
  };
}

/**
 * Depois da prova, com o envio aberto: a pessoa marca o que respondeu na prova
 * oficial e vê a correção contra o gabarito oficial.
 */
function gabaritoContent(
  shortName: string,
  year: number,
  totalQuestions: number,
): FunnelContent {
  return {
    seo: {
      title: `Gabarito ${shortName} ${year}: envie suas respostas e veja sua nota`,
      description: `Marque o que você respondeu nas ${totalQuestions} questões da prova da ${shortName} e veja sua correção contra o gabarito oficial.`,
    },
    hero: {
      title: `Envie seu gabarito da ${shortName} e veja sua nota`,
      lead: `Marque o que você respondeu nas ${totalQuestions} questões da prova e confira sua correção, questão a questão, contra o gabarito oficial.`,
      ctaLabel: 'Enviar meu gabarito',
      secondaryLabel: 'Enviar meu gabarito',
    },
    stepsSubtitle: 'Três passos entre a prova e a sua correção.',
    steps: [
      {
        title: 'Marque suas respostas',
        description: `A folha tem as ${totalQuestions} questões da ${shortName}. O que você marca fica salvo no navegador até enviar.`,
      },
      {
        title: 'Entre ou crie sua conta',
        description:
          'Ao enviar, entre com sua conta ou faça um cadastro rápido. Vale tendo feito o ensaio ou não.',
      },
      {
        title: 'Veja sua correção',
        description:
          'Sua nota bruta e cada questão ao lado do gabarito oficial, assim que ele for publicado.',
      },
    ],
    featuresTitle: 'O que a correção mostra',
    features: [
      {
        icon: 'chart',
        title: 'Sua nota bruta',
        description:
          'Acertos sobre as questões válidas: as anuladas saem da conta.',
      },
      {
        icon: 'target',
        title: 'Questão a questão',
        description:
          'O gabarito oficial ao lado do que você marcou, com acertos, erros e brancos.',
      },
      {
        icon: 'clock',
        title: 'No seu ritmo',
        description:
          'Pode parar e voltar depois: o que você marcou fica salvo neste navegador.',
      },
    ],
    closer: {
      title: 'Já fez a prova?',
      subtitle: `Envie seu gabarito da ${shortName} e veja sua correção.`,
    },
    faq: [
      {
        group: 'Sobre o gabarito',
        items: [
          {
            q: 'Preciso marcar todas as questões?',
            a: 'Não. Deixe em branco o que você não marcou na prova; em branco conta como erro na nota.',
          },
          {
            q: 'Perco minhas respostas se fechar a página?',
            a: 'Não. Elas ficam salvas neste navegador até você enviar o gabarito.',
          },
          {
            q: 'Posso enviar de novo?',
            a: 'Pode. Enviar de novo substitui o gabarito anterior.',
          },
          {
            q: 'E se o gabarito oficial ainda não saiu?',
            a: 'Envie agora: sua correção aparece no painel assim que ele for publicado.',
          },
        ],
      },
    ],
  };
}
